import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { toast as sonnerToast } from 'sonner';
import { PageableResponse } from '../types/backend';

// Base API configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api';

// Create axios instance with default configuration
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for adding auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling common errors
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error) => {
    // Only log in development mode
    if (import.meta.env.DEV) {
      console.error('API Error:', {
        status: error.response?.status,
        url: error.config?.url,
        message: error.message
      });
    }
    
    if (error.response?.status === 401) {
      // Centralize logout without hard navigation; let route guards handle redirect
      try {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
      } catch {}
      // Clear header for subsequent requests
      try { setAuthToken(null as any); } catch {}
      // User feedback
      try {
        sonnerToast.error('Session expired. Please log in again.');
      } catch {}
      // Notify app to logout
      window.dispatchEvent(new Event('app:auth-logout'));
    }
    
    return Promise.reject(error);
  }
);

// Generic API response type (matches backend ApiResponse)
export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
  errors?: Record<string, string>;
  timestamp?: string;
}

// Generic API error type
export interface ApiError {
  message: string;
  code?: string;
  details?: any;
}

// Base service class with common CRUD operations
export abstract class BaseService<T, CreateT = Partial<T>, UpdateT = Partial<T>> {
  protected abstract endpoint: string;

  async getAll(params?: Record<string, any>): Promise<PageableResponse<T>> {
    try {
      const response = await apiClient.get<ApiResponse<PageableResponse<T>>>(this.endpoint, { params });
      return response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getById(id: string | number): Promise<T> {
    try {
      const response = await apiClient.get<ApiResponse<T>>(`${this.endpoint}/${id}`);
      return response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async create(data: CreateT): Promise<T> {
    try {
      const response = await apiClient.post<ApiResponse<T>>(this.endpoint, data);
      return response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async update(id: string | number, data: UpdateT): Promise<T> {
    try {
      const response = await apiClient.put<ApiResponse<T>>(`${this.endpoint}/${id}`, data);
      return response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async delete(id: string | number): Promise<void> {
    try {
      await apiClient.delete(`${this.endpoint}/${id}`);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  protected handleError(error: any): ApiError {
    if (error.response?.data) {
      return {
        message: error.response.data.message || 'An error occurred',
        code: error.response.data.code,
        details: error.response.data.details,
      };
    }
    return {
      message: error.message || 'Network error occurred',
    };
  }
}

// Date range filter interface
export interface DateRangeFilter {
  startDate: string;
  endDate: string;
}

// Pagination interface
export interface PaginationParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// Add these new functions to manage the auth token header
export const setAuthToken = (token: string | null) => {
  if (token) {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete apiClient.defaults.headers.common['Authorization'];
  }
};

export { apiClient };
export default apiClient;
